import { Mesh, Program, Renderer, Texture, Triangle } from "ogl";

import { logger } from "@utilities/logger";

import RAF from "@/app/_ui/components/core/Orb/glxp/utils/RAF";

const VERTEX = /* glsl */ `#version 300 es
precision highp float;
in vec2 uv;
in vec3 position;
out vec2 vUv;
void main() {
    vUv = vec2(uv.x, 1.0 - uv.y);
    gl_Position = vec4(position, 1.0);
}
`;

const FRAGMENT = /* glsl */ `#version 300 es
precision highp float;
in vec2 vUv;

uniform sampler2D uSource;
uniform vec2 uResolution;
uniform vec2 uTextureResolution;
uniform float uProgress;
uniform vec2 uRingCenter;

out vec4 outColor;

vec2 getCoverUv(vec2 uv, vec2 containerRes, vec2 textureRes) {
    float containerAspect = containerRes.x / containerRes.y;
    float textureAspect = textureRes.x / textureRes.y;
    vec2 scale = vec2(1.0);
    if (containerAspect > textureAspect) scale.y = textureAspect / containerAspect;
    else scale.x = containerAspect / textureAspect;
    return (uv - 0.5) * scale + 0.5;
}

float quadraticOut(float t) {
    return -t * (t - 2.0);
}

void main() {
    vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
    vec2 coverUv = getCoverUv(uv, uResolution, uTextureResolution);
    float p = quadraticOut(uProgress);

    vec2 center = vec2(uRingCenter.x, 1.0 - uRingCenter.y);
    vec2 centered = uv - center;
    centered.x *= uResolution.x / uResolution.y;
    float dist = length(centered);

    float radius = p * 1.2;
    float ring = smoothstep(0.35, 0.0, abs(dist - radius)) * (1.0 - p);

    vec2 fromCenter = uv - center;
    float distFromCenter = length(fromCenter);
    vec2 normal = fromCenter / max(distFromCenter, 1e-4);
    float centerDamp = smoothstep(0.0, 0.2, distFromCenter);

    vec3 source = texture(uSource, coverUv + normal * ring * 0.05 * centerDamp).rgb;
    vec3 color = source + vec3(ring * ring * 0.35 * (1.0 - p));

    outColor = vec4(color, 1.0);
}
`;

const TRANSITION_DURATION = 1;

export type RingTransitionSource = HTMLVideoElement | HTMLImageElement | HTMLCanvasElement;

class RingTransitionFX {
  isSupported = false;

  private container: HTMLElement;
  private source: RingTransitionSource;
  private dpr: number;
  private rafId: string;

  private renderer: Renderer | null = null;
  private program: Program | null = null;
  private mesh: Mesh | null = null;
  private texture: Texture | null = null;

  private time = 0;
  private transitionStart: number | null = null;
  private isPlaying = false;
  private wasPlayingBeforeHidden = false;
  private lastUploadedTime = -1;
  private staticUploaded = false;
  private hasRendered = false;
  private onReady: (() => void) | null = null;

  private resizeObserver: ResizeObserver | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private removeSourceReadyListener: (() => void) | null = null;

  constructor(container: HTMLElement, source: RingTransitionSource, options: { onReady?: () => void } = {}) {
    this.container = container;
    this.source = source;
    this.dpr = Math.min(window.devicePixelRatio, 2);
    this.rafId = `ring-transition-fx-${Math.random().toString(36).slice(2)}`;
    this.onReady = options.onReady ?? null;

    try {
      this.init();
      this.isSupported = true;
    } catch (error) {
      logger.warn("[RingTransitionFX] WebGL2 init failed, falling back to native source", error);
      this.dispose();
    }
  }

  private init() {
    const { offsetWidth: w, offsetHeight: h } = this.container;

    this.renderer = new Renderer({
      dpr: this.dpr,
      width: w,
      height: h,
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      powerPreference: "high-performance",
    });

    const gl = this.renderer.gl;
    const canvas = gl.canvas as HTMLCanvasElement;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.display = "block";
    this.container.appendChild(canvas);

    this.texture = new Texture(gl, {
      image: this.source,
      generateMipmaps: false,
      minFilter: gl.LINEAR,
      magFilter: gl.LINEAR,
      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,
    });

    const [tw, th] = this.getSourceSize();
    this.program = new Program(gl, {
      vertex: VERTEX,
      fragment: FRAGMENT,
      uniforms: {
        uSource: { value: this.texture },
        uProgress: { value: 1 },
        uRingCenter: { value: [0.5, 0.5] },
        uResolution: { value: [w * this.dpr, h * this.dpr] },
        uTextureResolution: { value: [tw, th] },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    this.mesh = new Mesh(gl, { geometry: new Triangle(gl), program: this.program });

    this.attachSourceReadyListener();

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);

    this.intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          if (this.wasPlayingBeforeHidden) this.play();
        } else {
          this.wasPlayingBeforeHidden = this.isPlaying;
          this.stop();
        }
      },
      { threshold: 0 },
    );
    this.intersectionObserver.observe(this.container);
  }

  private attachSourceReadyListener() {
    const onReady = () => {
      if (!this.program) return;
      this.program.uniforms.uTextureResolution.value = this.getSourceSize();
    };

    if (this.source instanceof HTMLVideoElement) {
      const el = this.source;
      el.addEventListener("loadedmetadata", onReady);
      this.removeSourceReadyListener = () => el.removeEventListener("loadedmetadata", onReady);
      if (el.readyState >= 1 /* HAVE_METADATA */) onReady();
      return;
    }

    if (this.source instanceof HTMLImageElement) {
      const el = this.source;
      el.addEventListener("load", onReady);
      this.removeSourceReadyListener = () => el.removeEventListener("load", onReady);
      if (el.complete && el.naturalWidth > 0) onReady();
      return;
    }

    // HTMLCanvasElement / ImageBitmap: dimensions are known at construction.
  }

  private getSourceSize(): [number, number] {
    if (this.source instanceof HTMLVideoElement) {
      return [this.source.videoWidth || 1, this.source.videoHeight || 1];
    }
    if (this.source instanceof HTMLImageElement) {
      return [this.source.naturalWidth || 1, this.source.naturalHeight || 1];
    }
    return [this.source.width || 1, this.source.height || 1];
  }

  private isSourceReady() {
    if (this.source instanceof HTMLVideoElement) return this.source.readyState >= 2; // HAVE_CURRENT_DATA
    if (this.source instanceof HTMLImageElement) return this.source.complete && this.source.naturalWidth > 0;
    return true;
  }

  play() {
    if (this.isPlaying || !this.isSupported) return;
    this.isPlaying = true;
    RAF.subscribe(this.rafId, (dt: number) => this.update(dt / 1000));
  }

  stop() {
    if (!this.isPlaying) return;
    this.isPlaying = false;
    RAF.unsubscribe(this.rafId);
  }

  triggerTransition(centerUv: [number, number] = [0.5, 0.5]) {
    if (!this.program) return;
    this.program.uniforms.uRingCenter.value = [centerUv[0], centerUv[1]];
    this.transitionStart = this.time;
  }

  resize() {
    if (!this.renderer || !this.program) return;
    const { offsetWidth: w, offsetHeight: h } = this.container;
    this.renderer.setSize(w, h);
    this.program.uniforms.uResolution.value = [w * this.dpr, h * this.dpr];
  }

  update(dt: number) {
    if (!this.renderer || !this.program || !this.mesh) return;

    this.time += dt;
    this.program.uniforms.uProgress.value =
      this.transitionStart === null ? 1 : Math.min((this.time - this.transitionStart) / TRANSITION_DURATION, 1);

    let uploadedThisFrame = false;
    if (this.texture && this.isSourceReady()) {
      if (this.source instanceof HTMLVideoElement) {
        const advanced =
          !this.source.paused || this.source.seeking || this.source.currentTime !== this.lastUploadedTime;
        if (advanced) {
          this.texture.needsUpdate = true;
          this.lastUploadedTime = this.source.currentTime;
          uploadedThisFrame = true;
        }
      } else if (!this.staticUploaded) {
        this.texture.needsUpdate = true;
        this.staticUploaded = true;
        uploadedThisFrame = true;
      }
    }

    // Skip rendering until the first frame is on the GPU, otherwise we paint
    // opaque black over the underlying <video> — most visible on mobile where
    // the first frame doesn't decode until the user actually plays.
    if (!this.hasRendered && !uploadedThisFrame) return;

    this.renderer.render({ scene: this.mesh });

    if (!this.hasRendered) {
      this.hasRendered = true;
      this.onReady?.();
    }
  }

  dispose() {
    this.stop();

    this.onReady = null;
    this.removeSourceReadyListener?.();
    this.removeSourceReadyListener = null;
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();

    const canvas = this.renderer?.gl.canvas as HTMLCanvasElement | undefined;
    canvas?.parentNode?.removeChild(canvas);

    this.renderer = null;
    this.program = null;
    this.mesh = null;
    this.texture = null;
    this.resizeObserver = null;
    this.intersectionObserver = null;
  }
}

export default RingTransitionFX;
